import React, { useRef, useState } from 'react';
import { Upload, Paperclip, Trash2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { File as ClientFile } from '../../../types';

interface DocumentTabProps {
  submissionId: string;
  files: ClientFile[];
  onRefresh: () => void;
  onDeleteFile: (fileId: string, filePath: string) => Promise<void>;
}

export default function DocumentTab({ 
  submissionId, 
  files, 
  onRefresh,
  onDeleteFile 
}: DocumentTabProps) {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    await handleUploadFiles(files);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    await handleUploadFiles(files);
  };

  const handleUploadFiles = async (files: File[]) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('submissionId', submissionId);
      formData.append('content', '');

      for (const file of files) {
        formData.append('files', file);
      }

      const response = await fetch('/api/add-comment', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error();

      toast.success('Fichiers ajoutés avec succès');
      onRefresh();
    } catch (err) {
      toast.error('Erreur lors de l\'ajout des fichiers');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
  <div className="space-y-4">
    <div
      className="w-full h-32 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition"
      onClick={() => fileInputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <Upload className="h-8 w-8 text-gray-400 mb-1" />
      <p className="text-gray-600 text-sm">
        {isUploading ? 'Upload en cours...' : 'Glissez-déposez ou cliquez pour ajouter des fichiers'}
      </p>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        onChange={handleFileSelect}
        disabled={isUploading}
      />
    </div>

    <div className="space-y-2">
      {files.map((file) => (
        <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center">
            <Paperclip className="h-5 w-5 text-gray-400 mr-2" />
            <div>
              <p className="text-sm font-medium text-gray-900">{file.name}</p>
              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <a
              href={file.signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              Voir
            </a>
            <button
              onClick={() => onDeleteFile(file.id, file.file_path)}
              className="text-red-600 hover:text-red-800"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>

    {/* ✅ Nouveau bouton "Joindre des fichiers" */}
    <div className="pt-4 text-center">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        disabled={isUploading}
      >
        <Paperclip className="h-4 w-4 mr-2" />
        {isUploading ? 'Téléversement en cours...' : 'Joindre des fichiers'}
      </button>
    </div>
  </div>
);
}